import { useState, useEffect } from 'react';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';
import { canAccessClinicalEvolutions, type SystemRole } from '../config/permissions';

let cachedHideWithoutProfessional: boolean | undefined;
let cachePromise: Promise<boolean> | null = null;

async function fetchHideEvolutionsWithoutProfessional(): Promise<boolean> {
  if (cachedHideWithoutProfessional !== undefined) return cachedHideWithoutProfessional;
  if (!cachePromise) {
    cachePromise = axios
      .get(getApiUrl('/clinic/settings'))
      .then((res) => {
        cachedHideWithoutProfessional = Boolean(
          res.data?.data?.hide_clinical_evolutions_without_professional,
        );
        return cachedHideWithoutProfessional;
      })
      .catch(() => {
        cachedHideWithoutProfessional = false;
        return false;
      });
  }
  return cachePromise;
}

export function invalidateClinicalEvolutionsAccessCache(): void {
  cachedHideWithoutProfessional = undefined;
  cachePromise = null;
}

/** Evoluções SOAP no painel e rotas — respeita recepção e config da clínica. */
export function useClinicalEvolutionsAccess(): { canAccess: boolean; loading: boolean } {
  const { user } = useAuth();
  const role = user?.role as SystemRole | undefined;
  const professionalId = user?.professional_id ?? null;

  const [hideWithoutProfessional, setHideWithoutProfessional] = useState(
    cachedHideWithoutProfessional ?? false,
  );
  const [loading, setLoading] = useState(cachedHideWithoutProfessional === undefined);

  useEffect(() => {
    let cancelled = false;

    const load = () => {
      fetchHideEvolutionsWithoutProfessional().then((hide) => {
        if (cancelled) return;
        setHideWithoutProfessional(hide);
        setLoading(false);
      });
    };

    load();

    const onUnitChanged = () => {
      invalidateClinicalEvolutionsAccessCache();
      setLoading(true);
      load();
    };
    const onSettingsSaved = () => {
      invalidateClinicalEvolutionsAccessCache();
      setLoading(true);
      load();
    };

    window.addEventListener('unit:changed', onUnitChanged);
    window.addEventListener('clinic-settings:saved', onSettingsSaved);
    return () => {
      cancelled = true;
      window.removeEventListener('unit:changed', onUnitChanged);
      window.removeEventListener('clinic-settings:saved', onSettingsSaved);
    };
  }, []);

  const canAccess = canAccessClinicalEvolutions(role, {
    professionalId,
    hideWithoutProfessional,
  });

  return { canAccess, loading };
}

export default useClinicalEvolutionsAccess;
